import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  Col,
  Row,
  UiButton,
  UiFormControl,
  UiFormErrorMessage,
  UiQuestionnaireLayout,
  UiText,
  UiTextarea,
} from '~/components/elements';
import {NO_OPTION, WEIGHT_LOSS_KEYS, YES_OPTION} from '~/constants/medical-questionnaires';
import {useInput} from '~/hooks';
import {setNextQuestionnaire, setQuestionnaireAnswer} from '~/redux/questionnaires/slice';
import {Relation} from '~/redux/questionnaires/types';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';
import {spacing} from '~/utils/theme/spacing';

const Questionnaire17Screen = () => {
  const dispatch: AppDispatch = useDispatch();
  const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);
  const questionnaires = medicalQuestionnaire?.questionnaires;
  const questionnaire = questionnaires?.find(i => i.key === WEIGHT_LOSS_KEYS.DID_MEDICATION_WORK);

  const currentAnswer = useSelector(
    (state: RootState) => state.questionnaires?.collectData[WEIGHT_LOSS_KEYS.DID_MEDICATION_WORK]?.data,
  );

  const {inputs, setInputs} = useInput({
    hasSideEffects: currentAnswer?.hasSideEffects || '',
    selectedOption: currentAnswer?.selectedOption || '',
    sideEffects: currentAnswer?.sideEffects || '',
    weightLose: currentAnswer?.weightLose || '',
  });
  const selectedOptionRelations = questionnaire?.options?.find(
    option => option.value === inputs?.selectedOption,
  )?.relations;

  const onClickSubmit = () => {
    let data: Record<string, string> = {
      selectedOption: inputs?.selectedOption,
      hasSideEffects: inputs?.hasSideEffects,
    };

    if (inputs?.selectedOption === YES_OPTION) {
      data.weightLose = inputs?.weightLose;
    }

    if (inputs?.hasSideEffects === YES_OPTION) {
      data.sideEffects = inputs?.sideEffects;
    }
    dispatch(
      setQuestionnaireAnswer({
        key: WEIGHT_LOSS_KEYS.DID_MEDICATION_WORK,
        index: questionnaire?.index || '',
        data,
      }),
    );
    dispatch(setNextQuestionnaire({value: WEIGHT_LOSS_KEYS.OTHER_CONDITION_OR_MEDICAL_HISTORY}));
  };

  const isDisable =
    !inputs?.selectedOption ||
    !inputs?.hasSideEffects ||
    (inputs?.selectedOption === YES_OPTION && !inputs?.weightLose) ||
    (inputs?.hasSideEffects === YES_OPTION && !inputs?.sideEffects);

  const renderRelation = (key: string, value: string, relations?: Relation[]) => {
    if (relations) {
      return (
        <>
          {relations?.map(relation => (
            <Col w="100%" key={relation?.key}>
              <UiText fontWeight={700} fontSize="16px" mt={3}>
                {relation?.questions?.default}
              </UiText>
              <UiFormControl>
                <UiTextarea
                  mt={spacing.small}
                  bg="white"
                  borderRadius={spacing.tiny}
                  placeholder="Type your response here..."
                  defaultValue={value}
                  onChange={({target}) => setInputs({[key]: target.value})}
                />
              </UiFormControl>
            </Col>
          ))}
        </>
      );
    }
    return null;
  };

  return (
    <UiQuestionnaireLayout
      backIcon
      handleOnNext={onClickSubmit}
      actions={questionnaire?.actions}
      checkoutStep={questionnaire?.checkoutStep}
      disableNext={isDisable}
    >
      <Col align="center" textAlign="center">
        <UiText fontWeight={700} fontSize="16px" mt={10} mb={4}>
          {questionnaire?.questions?.default}
        </UiText>
        <Row justifyContent="center" w="100%">
          {questionnaire?.options?.map(value => (
            <UiButton
              onClick={() => setInputs({selectedOption: value?.value})}
              key={`${value.label}-${value.value}`}
              title={value?.label}
              variant={inputs?.selectedOption === value.value ? 'solid' : 'toggle'}
              w="100%"
              mb={4}
              mx={1}
              mt={2}
            />
          ))}
        </Row>
        {renderRelation('weightLose', inputs?.weightLose, selectedOptionRelations)}
        {questionnaire?.sideEffectOptions?.map(value => {
          const sideEffectRelations = value?.options?.find(
            option => option.value === inputs?.hasSideEffects,
          )?.relations;
          return (
            <Col key={value?.key} w="100%">
              <UiText fontWeight={700} fontSize="16px" mt={10} mb={4}>
                {value?.questions?.default}
              </UiText>
              <Row justifyContent="center" w="100%">
                {value?.options?.map(option => (
                  <UiButton
                    onClick={() => setInputs({hasSideEffects: option?.value})}
                    key={`${option.label}-${option.value}`}
                    title={option?.label}
                    variant={inputs?.hasSideEffects === option.value ? 'solid' : 'toggle'}
                    w="100%"
                    mb={4}
                    mx={1}
                    mt={2}
                  />
                ))}
              </Row>
              {renderRelation('sideEffects', inputs?.sideEffects, sideEffectRelations)}
            </Col>
          );
        })}
      </Col>
    </UiQuestionnaireLayout>
  );
};

export default Questionnaire17Screen;
